import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, User, Shield, Trophy, FileText, Info } from 'lucide-react';

interface RoleInstructionsProps {
  userRole?: string;
}

export const RoleInstructions: React.FC<RoleInstructionsProps> = ({ userRole }) => {
  const getRoleInfo = () => {
    switch (userRole) {
      case 'coach':
        return {
          icon: Trophy,
          title: 'Coach Responsibilities',
          description: 'As a coach, you manage your team\'s roster and readiness',
          tasks: [
            'Add and manage players in the "Coach Management" section (orange)',
            'Set player positions and starting lineup',
            'Update player status (present, injured, no-show, etc.)',
            'Confirm team readiness before game time',
            'Players update their own status in the "Player Status" section (blue)'
          ]
        };
      case 'player':
        return {
          icon: User,
          title: 'Player Responsibilities',
          description: 'As a player, you confirm your attendance and readiness',
          tasks: [
            'Update your attendance status in the "Player Status" section (blue)',
            'Confirm if you\'re ready to play, injured, or unavailable',
            'Provide injury details if applicable',
            'Add emergency contact information',
            'Your coach manages the team roster in their section'
          ]
        };
      case 'team-owner':
        return {
          icon: Users,
          title: 'Team Owner Responsibilities',
          description: 'As a team owner, you manage team operations',
          tasks: [
            'Manage team roster and coaching staff',
            'Oversee team preparation and strategy',
            'Coordinate with coaches on player management',
            'Ensure team compliance with game requirements'
          ]
        };
      case 'venue':
        return {
          icon: Shield,
          title: 'Venue Manager Responsibilities',
          description: 'As a venue manager, you handle facility and logistics',
          tasks: [
            'Confirm venue availability and setup',
            'Manage facility requirements and equipment',
            'Coordinate with teams and officials',
            'Ensure venue compliance and safety'
          ]
        };
      default:
        return {
          icon: Info,
          title: 'Form Instructions',
          description: 'Understanding your role in game preparation',
          tasks: [
            'Complete sections relevant to your role',
            'Coaches manage team rosters and player status',
            'Players update their individual attendance and readiness',
            'Each role has specific responsibilities marked by colors'
          ]
        };
    }
  };

  const roleInfo = getRoleInfo();
  const Icon = roleInfo.icon;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="h-5 w-5" />
          {roleInfo.title}
          {userRole && (
            <Badge variant="outline" className="ml-2">
              {userRole.replace('-', ' ').toUpperCase()}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {roleInfo.description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {roleInfo.tasks.map((task, index) => (
            <li key={index} className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
              <span className="text-sm">{task}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};